"use client"

import { useRef, useState, useEffect } from 'react'
import { Volume2, VolumeX } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { useMediaContext } from '@/context/MediaContext'
// Importing the stylex styles we converted
import { videoPageStyles } from '@/styles/VideoPage.stylex'

interface HeroVideoPlayerProps {
    videoSrc: string
    posterSrc: string
}

export function HeroVideoPlayer({ videoSrc, posterSrc }: HeroVideoPlayerProps) {
    const heroVideoRef = useRef<HTMLVideoElement>(null)
    const [isMuted, setIsMuted] = useState(true) // Start muted (browser autoplay policy)
    const [isHovered, setIsHovered] = useState(false) // Required for StyleX child hover effects
    const { registerMediaElement, unregisterMediaElement, onMediaPlay } = useMediaContext()

    // Register video element with MediaContext
    useEffect(() => {
        if (heroVideoRef.current) {
            registerMediaElement('hero-video', heroVideoRef.current)
        }
        return () => unregisterMediaElement('hero-video')
    }, [registerMediaElement, unregisterMediaElement])

    const handleOverlayClick = () => {
        if (heroVideoRef.current) {
            const newMuted = !isMuted
            heroVideoRef.current.muted = newMuted
            setIsMuted(newMuted)

            // When unmuting, pause all other media
            if (!newMuted) {
                onMediaPlay('hero-video')
            }
        }
    }

    return (
        <div {...stylex.props(videoPageStyles.heroBackground)}>
            <video
                ref={heroVideoRef}
                src={videoSrc}
                poster={posterSrc}
                {...stylex.props(videoPageStyles.heroBgVideo)}
                autoPlay
                loop
                muted // Must start muted for browser autoplay policy
                playsInline
            />
            <div
                {...stylex.props(videoPageStyles.heroOverlay)}
                onClick={handleOverlayClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div
                    {...stylex.props(
                        videoPageStyles.muteIndicator,
                        isHovered && videoPageStyles.muteIndicatorHover
                    )}
                >
                    {isMuted ? <VolumeX size={32} /> : <Volume2 size={32} />}
                </div>
            </div>
        </div>
    )
}